import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import _ from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { HrpsApiClient } from '../../../clients';
import { RootState } from '../../../redux/types';
import { IUser } from '../../../redux/types/user';
import { ResellerClaimRegexPattern } from '../../../types';
import { Logger } from '../../../utils/logger';
import { Alert, Button, useTranslation } from '../../blocks';
import { usePageMessaging } from '../../composites';
import { useModal } from '../../composites/Modal';
import { RegexPatternModal } from '../../composites/RegexPatternModal';
import { ResellerClaimRegexTable } from '../../composites/ResellerClaimRegexTable';

type ResellerClaimRegexBodyProps = ConnectedProps<typeof connector>;

const ResellerClaimRegexBody: React.FC<ResellerClaimRegexBodyProps> = ({ user, selectedReseller }) => {
    const { t } = useTranslation('resellerClaimRegexBody');
    const { t: tCommon } = useTranslation('common');
    const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
    const [hasError, setHasError] = useState<boolean>(false);
    const [patterns, setPatterns] = useState<ResellerClaimRegexPattern[]>([]);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    const { showSuccess, showError } = usePageMessaging();
    const modal = useModal();

    const editRoles = ['amazonAdmin', 'amazonCreator'];
    const canEdit = !!user && _.intersection(user.roles, editRoles).length > 0;

    const fetchPatterns = useCallback(async () => {
        setHasError(false);
        try {
            const hrpsApiClient = new HrpsApiClient();
            const resellerPatterns = selectedReseller
                ? await hrpsApiClient.getResellerClaimRegexPatterns(selectedReseller.resellerId)
                : [];
            setPatterns(resellerPatterns);
        } catch (error) {
            Logger.debug(`Error fetching reseller regex patterns: ${error}`);
            setHasError(true);
            setPatterns([]);
        } finally {
            setIsInitialLoading(false);
        }
    }, [selectedReseller]);

    useEffect(() => {
        fetchPatterns();
    }, [fetchPatterns]);

    const handleAddPattern = useCallback(
        async (pattern: ResellerClaimRegexPattern) => {
            try {
                const hrpsApiClient = new HrpsApiClient();
                await hrpsApiClient.createResellerClaimRegexPattern(pattern);
                setIsModalOpen(false);
                showSuccess(t('createSuccess-successMessage'));
                await fetchPatterns();
            } catch (error) {
                Logger.debug(`Error creating reseller regex pattern: ${error}`);
                showError(t('createError-errorMessage'));
            }
        },
        [fetchPatterns, showSuccess, showError, t]
    );

    const handleDeletePatternImpl = useCallback(
        (pattern: ResellerClaimRegexPattern) => {
            modal
                .confirm(
                    t('deleteConfirm-title'),
                    t('deleteConfirm-message', {
                        regexPattern: pattern.regexPattern,
                        resellerName: pattern.resellerName,
                    }),
                    tCommon('yes-text'),
                    tCommon('cancel-text')
                )
                .applied(async () => {
                    try {
                        const hrpsApiClient = new HrpsApiClient();
                        await hrpsApiClient.deleteResellerClaimRegexPattern(
                            pattern.resellerId,
                            pattern.regexPattern,
                            pattern.subResellerName
                        );
                        showSuccess(t('deleteSuccess-successMessage'));
                        await fetchPatterns();
                    } catch (error) {
                        Logger.debug(`Error deleting reseller regex pattern: ${error}`);
                        showError(t('deleteError-errorMessage'));
                    }
                });
        },
        [modal, fetchPatterns, showSuccess, showError, t, tCommon]
    );

    const handleDeletePatternRef = useRef(handleDeletePatternImpl);
    handleDeletePatternRef.current = handleDeletePatternImpl;

    const handleDeletePattern = useCallback((pattern: ResellerClaimRegexPattern) => {
        handleDeletePatternRef.current(pattern);
    }, []);

    return (
        <Column spacing={'400'}>
            {canEdit && !isInitialLoading && (
                <Row alignmentHorizontal={'right'}>
                    <Button
                        type={'primary'}
                        size={'small'}
                        onClick={() => setIsModalOpen(true)}
                        data-testid={'add-pattern-button'}
                    >
                        {t('addPattern-buttonLabel')}
                    </Button>
                </Row>
            )}

            {isInitialLoading && (
                <Row alignmentHorizontal={'center'}>
                    <Loader size={'large'} data-testid={'loading-indicator'} />
                </Row>
            )}

            {hasError && (
                <Alert type={'error'} data-testid={'fetch-error-message'}>
                    {t('fetchError-errorMessage')}
                </Alert>
            )}

            {!isInitialLoading && (
                <ResellerClaimRegexTable patterns={patterns} onDeletePattern={handleDeletePattern} canEdit={canEdit} />
            )}

            {selectedReseller && (
                <RegexPatternModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleAddPattern}
                    selectedReseller={selectedReseller}
                    userId={user?.username || ''}
                    existingPatterns={patterns}
                />
            )}
        </Column>
    );
};

const mapStateToProps = ({ resellerReducer, userReducer }: RootState) => {
    return {
        user: userReducer.user as IUser,
        selectedReseller: resellerReducer.selectedReseller.value!,
    };
};

const connector = connect(mapStateToProps);
export default connector(ResellerClaimRegexBody);
